import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ShoppingBag, Plus, Check, Sparkles } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import SEO from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_MAP = { "for-her": "For Her", "accessories": "Accessories", "jewellery": "Jewellery For Her" };
function displayCategory(item) {
  const cat = item.category || "";
  if (cat && !/^CAT-/i.test(cat) && !/^\d+$/.test(cat)) return cat;
  const slug = (item.categories || [])[0];
  return slug ? (CATEGORY_MAP[slug] || slug.replace(/-/g, " ").replace(/\b\w/g, c => c.toUpperCase())) : cat;
}

export default function LookDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [look, setLook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addedItems, setAddedItems] = useState(new Set());
  const [addingAll, setAddingAll] = useState(false);

  useEffect(() => {
    fetch(`${API}/ai/looks/${id}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(d => setLook(d))
      .catch(() => navigate("/looks"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Microsoft Clarity — look viewed event
  useEffect(() => {
    if (look && window.clarity) {
      window.clarity("set", "look_name", look.name || "");
      window.clarity("event", "look_viewed");
    }
  }, [look]);

  // Tier discount calculation
  const itemCount = look?.items?.length || 0;
  const tierDiscount = itemCount >= 4 ? 20 : itemCount === 3 ? 15 : itemCount === 2 ? 10 : 0;
  const totalOriginal4 = look?.total_price_4_day || 0;
  const totalDiscounted4 = tierDiscount > 0 ? Math.round(totalOriginal4 * (1 - tierDiscount / 100)) : totalOriginal4;

  const handleAddItem = async (item) => {
    try {
      await addToCart({
        outfit_id: item.id,
        event_date: "",
        duration: 4,
        delivery_method: "pickup",
      });
      setAddedItems(prev => new Set([...prev, item.id]));
      toast.success(`${item.name} added to cart`);
    } catch {
      toast.error("Failed to add item");
    }
  };

  const handleAddAll = async () => {
    setAddingAll(true);
    const available = (look?.items || []).filter(i => i.available);
    let added = 0;
    for (const item of available) {
      if (addedItems.has(item.id)) continue;
      try {
        await addToCart({
          outfit_id: item.id,
          event_date: "",
          duration: 4,
          delivery_method: "pickup",
        });
        setAddedItems(prev => new Set([...prev, item.id]));
        added++;
      } catch { /* continue */ }
    }
    setAddingAll(false);
    if (added > 0) toast.success(`Added ${added} items to cart`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF8F0]">
        <Navbar />
        <div className="max-w-[1200px] mx-auto px-4 py-16">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-[#E8E0D5] rounded w-1/3" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => <div key={i} className="aspect-[3/4] bg-[#E8E0D5]" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!look) return null;

  // Universal main-vs-accessory classifier — trust the backend `slot_role`
  // when present (Phase 1 composition contract: 1 main + ≤3 accessories),
  // fall back to the legacy women's-category heuristic for older looks.
  const OUTFIT_CATS = ['lehengas', 'sarees', 'anarkalis & shararas', 'indo western', 'indo-western'];
  const isOutfit = (item) => {
    if (item?.slot_role === 'main') return true;
    if (item?.slot_role === 'accessory') return false;
    return OUTFIT_CATS.includes((item.category || '').toLowerCase());
  };

  // Find the main OUTFIT: prefer slot_role==='main', then hero_product_id, then items[0]
  const outfitItem = look.items?.find(i => i.slot_role === 'main')
    || look.items?.find(i => isOutfit(i))
    || look.items?.find(i => i.id === look.hero_product_id)
    || look.items?.[0];
  // Accessories = everything except the outfit item, capped at 3 to enforce
  // the Shop by Occasion contract (1 main + up to 3 accessories).
  const accessories = (look.items?.filter(i => i.id !== outfitItem?.id) || []).slice(0, 3);

  // Build item map for custom layout
  const itemMap = {};
  for (const item of look.items || []) itemMap[item.id] = item;

  return (
    <div className="min-h-screen bg-[#FAF8F0]">
      <SEO
        title={`${look.name} - Curated Look`}
        description={`Rent the ${look.name} styled look. ${look.items?.length || 0} designer Indian outfit pieces, perfectly coordinated. Available for hire in Melbourne.`}
        path={`/looks/${id}`}
        image={look.cover_image || outfitItem?.image || outfitItem?.images?.[0]}
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Looks", url: "/looks" }, { name: look.name }]}
      />
      <Navbar />
      <div className="max-w-[1200px] mx-auto px-4 lg:px-10 pt-6 pb-16">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-[12px] text-[#676664] mb-6">
          <Link to="/" className="hover:text-[#282828]">Home</Link>
          <span>/</span>
          <Link to="/looks" className="hover:text-[#282828]">Looks</Link>
          <span>/</span>
          <span className="text-[#282828]">{look.name}</span>
        </div>

        {/* Main Layout — Hero image + side items + info */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 mb-12">
          {/* Images Section — 7 cols */}
          <div className="lg:col-span-7 relative">
            {look.layout && look.layout.boxes && look.layout.boxes.length > 0 && look.layout.boxes.some(b => b.product_id && itemMap[b.product_id]) ? (
              /* Custom Layout Rendering */
              <div className="h-[500px] sm:h-[600px]" style={{
                display: "grid",
                gridTemplateColumns: `repeat(${look.layout.columns || 3}, 1fr)`,
                gridTemplateRows: `repeat(${Math.max(...look.layout.boxes.map(b => b.row + b.rowSpan), 1)}, 1fr)`,
                gap: "4px",
              }}>
                {look.layout.boxes.map(box => {
                  const item = itemMap[box.product_id];
                  return (
                    <Link key={box.id} to={item ? `/outfit/${item.id}` : "#"} style={{
                      gridColumn: `${box.col + 1} / span ${box.colSpan}`,
                      gridRow: `${box.row + 1} / span ${box.rowSpan}`,
                    }} className="overflow-hidden bg-[#f5f3f0] relative group block">
                      {item?.image ? (
                        <img src={item.image} alt={item.name || ""} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#676664]/30 text-xs">{box.label || ""}</div>
                      )}
                      {item && (
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                          <p className="text-[9px] text-white tracking-wider uppercase">{displayCategory(item)}</p>
                          <p className="text-[10px] text-white/80 truncate">{item.name}</p>
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
            ) : (
              /* Default Layout */
              <div className="flex gap-2 h-[500px] sm:h-[600px]">
                {/* LEFT: Outfit image — always clickable */}
                <Link to={`/outfit/${outfitItem?.id}`} className={`${accessories.length > 0 ? 'flex-[2.5]' : 'flex-1'} overflow-hidden bg-[#f5f3f0] block`}>
                  {outfitItem?.image ? (
                    <img src={outfitItem.image} alt={outfitItem.name || look.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#676664]/30">No Image</div>
                  )}
                </Link>
                {/* RIGHT: Accessories only */}
                {accessories.length > 0 && (
                  <div className="flex-1 flex flex-col gap-2">
                    {accessories.map(item => (
                      <Link key={item.id} to={`/outfit/${item.id}`} className="flex-1 overflow-hidden bg-[#f5f3f0] relative group">
                        {item.image ? (
                          <img src={item.image} alt={item.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#676664]/30 text-xs">{displayCategory(item)}</div>
                        )}
                        <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/50 to-transparent p-2">
                          <p className="text-[9px] text-white tracking-wider uppercase">{displayCategory(item)}</p>
                          <p className="text-[10px] text-white/80 truncate">{item.name}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info Section — 5 cols */}
          <div className="lg:col-span-5 flex flex-col justify-center">
            {(look.occasion || look.look_type) && (
              <span className="text-[10px] tracking-[3px] uppercase text-[#9A7B4F] mb-3">
                {[look.look_type, look.occasion].filter(Boolean).join(" · ")}
              </span>
            )}
            <h1 className="text-2xl sm:text-3xl text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              {look.name}
            </h1>
            <p className="text-sm text-[#676664] mt-3 leading-relaxed">{look.description}</p>

            {/* Styled With */}
            <div className="mt-5 pt-4 border-t border-[#282828]/8">
              <p className="text-[10px] tracking-[2px] uppercase text-[#676664] mb-2">Styled With</p>
              <div className="flex flex-wrap gap-1.5">
                {look.items?.map(item => (
                  <Link key={item.id} to={`/outfit/${item.id}`} className="text-[11px] text-[#282828] bg-[#f5f3f0] px-2.5 py-1 hover:bg-[#9A7B4F]/10 hover:text-[#9A7B4F] transition-colors">
                    {displayCategory(item)}: {item.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Pricing with tier discount */}
            <div className="mt-5 pt-4 border-t border-[#282828]/8">
              {tierDiscount > 0 && (
                <div className="flex items-center gap-2 mb-3" data-testid="tier-discount-badge">
                  <Sparkles className="w-3.5 h-3.5 text-[#9A7B4F]" />
                  <span className="bg-[#9A7B4F]/10 text-[#9A7B4F] text-[10px] tracking-wider uppercase px-2.5 py-1 font-medium">
                    {itemCount} items · Save {tierDiscount}%
                  </span>
                </div>
              )}
              <div className="flex items-baseline gap-3">
                {tierDiscount > 0 ? (
                  <>
                    <span className="text-2xl text-[#282828] font-medium">${totalDiscounted4}</span>
                    <span className="text-[14px] text-[#676664] line-through">${totalOriginal4}</span>
                    <span className="text-[12px] text-[#676664]">for 4 days</span>
                  </>
                ) : (
                  <>
                    <span className="text-2xl text-[#282828] font-medium">${totalOriginal4}</span>
                    <span className="text-[12px] text-[#676664]">for 4 days</span>
                  </>
                )}
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={handleAddAll}
                disabled={addingAll}
                className="bg-[#9A7B4F] hover:bg-[#8a6b3f] text-white px-8 h-11 text-[12px] tracking-wider uppercase"
                data-testid="rent-entire-look"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                {addingAll ? "Adding..." : `Rent Entire Look · $${totalDiscounted4}`}
              </Button>
              <Link to="/looks">
                <Button variant="outline" className="h-11 text-[12px] tracking-wider uppercase border-[#282828]/15 w-full sm:w-auto">
                  <ArrowLeft className="w-4 h-4 mr-2" /> More Looks
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* All Items Grid — full detail */}
        <div className="mb-12">
          <h2 className="text-lg text-[#282828] mb-6" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            Items in This Look
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="look-items">
            {look.items?.map(item => (
              <div key={item.id} className="group" data-testid={`look-item-${item.id}`}>
                <Link to={`/outfit/${item.id}`}>
                  <div className="aspect-[3/4] bg-[#f5f3f0] overflow-hidden mb-2 relative">
                    <img
                      src={item.image || ""}
                      alt={item.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    {!item.available && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <span className="text-white text-[11px] tracking-wider uppercase">Unavailable</span>
                      </div>
                    )}
                    <div className="absolute top-2 left-2 bg-white/90 px-2 py-0.5">
                      <span className="text-[9px] tracking-wider uppercase text-[#676664]">{displayCategory(item)}</span>
                    </div>
                  </div>
                </Link>
                <p className="text-[13px] text-[#282828] truncate">{item.name}</p>
                <p className="text-[11px] text-[#676664]">{item.color}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <span className="text-[12px] text-[#9A7B4F]">${item.price_4_day}</span>
                  <button
                    onClick={() => handleAddItem(item)}
                    disabled={!item.available || addedItems.has(item.id)}
                    className={`p-1.5 rounded-full transition-colors ${addedItems.has(item.id) ? "bg-green-50 text-green-600" : "bg-[#9A7B4F]/10 text-[#9A7B4F] hover:bg-[#9A7B4F]/20"} disabled:opacity-40`}
                    data-testid={`add-item-${item.id}`}
                  >
                    {addedItems.has(item.id) ? <Check className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
